import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import * as React from "react";

import { RobotoTooltip } from "@/shared/components";
import {
  ResourceView,
  useResourceView,
} from "@/shared/components/ResourceView";
import { MetricDefinitionRecord } from "@/shared/domain/metrics";
import {
  useAllMetricDefinitions,
  useDeleteMetricDefinition,
  useUpdateMetricDefinition,
} from "@/shared/domain/metrics/hooks";
import { ENTITY_ICONS } from "@/shared/foundation/icons";
import {
  DeleteOutlineIcon as DeleteIcon,
  EditIcon,
} from "@/shared/foundation/icons/mui";
import { Button } from "@/shared/foundation/primitives";
import {
  DataTable,
  buildColumnsForPreferences,
  createColumnHelper,
  deriveLoadingState,
  renderDashOrValue,
  type ColDef,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";

import styles from "./MetricDefinitionsTable.module.css";

export interface MetricDefinitionsTableProps {
  orgId: string | undefined;
}

const columnHelper = createColumnHelper<MetricDefinitionRecord>();

// Visible-by-default columns, in display order. `buildColumnsForPreferences`
// flips these to `hide: false` (overriding DataTable's `defaultColDef.hide:
// true`); the actions column isn't listed and carries its own `hide: false`.
const DEFAULT_VISIBLE_COLUMN_IDS = [
  "name",
  "description",
  "unit",
  "created",
  "modified",
];

/**
 * Management table for an org's metric definitions: lists all definitions and
 * supports editing a definition's description and unit (row click) or deleting
 * it. Deletes are gated behind a confirmation dialog because they are org-wide
 * and irreversible. The list is org-wide — definitions are not device/session
 * scoped.
 */
export function MetricDefinitionsTable({ orgId }: MetricDefinitionsTableProps) {
  const query = useAllMetricDefinitions(orgId);
  const { data, error } = query;
  const [editing, setEditing] = React.useState<MetricDefinitionRecord | null>(
    null,
  );
  const [deleting, setDeleting] = React.useState<MetricDefinitionRecord | null>(
    null,
  );

  // Columns start hidden under DataTable's `defaultColDef.hide: true`;
  // `buildColumnsForPreferences` flips the ids in DEFAULT_VISIBLE_COLUMN_IDS to
  // visible (same pattern as `AllOrgsViewer`). The actions column isn't a
  // preference, so it carries its own `hide: false`. Every column also sets
  // `suppressMovable: true`: this is a fixed admin layout, and without a
  // column-visibility picker a user who drags a column out has no way to bring
  // it back, so reordering/removal is disabled here. `DataTable` doesn't expose
  // `onRowClicked`, so editing is reachable two ways: clicking the Description
  // cell (a full-cell button) or the explicit edit action in the Actions
  // column. The Name cell is plain, read-only text.
  const columns = React.useMemo<ColDef<MetricDefinitionRecord>[]>(
    () =>
      buildColumnsForPreferences<MetricDefinitionRecord>({
        staticColumns: [
          columnHelper.accessor("name", {
            headerName: "Name",
            flex: 2,
            suppressMovable: true,
          }),
          columnHelper.accessor("description", {
            headerName: "Description",
            flex: 3,
            suppressMovable: true,
            cellRenderer: (
              params: ICellRendererParams<
                MetricDefinitionRecord,
                string | null
              >,
            ) => {
              const def = params.data;
              if (!def) {
                return null;
              }
              return (
                <button
                  type="button"
                  className={styles.cellButton}
                  onClick={() => setEditing(def)}
                >
                  {def.description ?? "—"}
                </button>
              );
            },
          }),
          columnHelper.accessor("unit", {
            headerName: "Unit",
            flex: 1,
            suppressMovable: true,
            cellRenderer: renderDashOrValue,
          }),
          columnHelper.timestamp("created", {
            headerName: "Created",
            flex: 1,
            suppressMovable: true,
            variant: "relative",
          }),
          columnHelper.timestamp("modified", {
            headerName: "Modified",
            flex: 1,
            suppressMovable: true,
            variant: "relative",
          }),
          {
            colId: "actions",
            headerName: "Actions",
            width: 120,
            hide: false,
            sortable: false,
            resizable: false,
            suppressMovable: true,
            cellClass: "flex items-center justify-end gap-2xs",
            cellRenderer: (
              params: ICellRendererParams<MetricDefinitionRecord>,
            ) => {
              const def = params.data;
              if (!def) {
                return null;
              }
              return (
                <>
                  <RobotoTooltip title="Edit definition">
                    <IconButton
                      size="small"
                      aria-label={`edit ${def.name}`}
                      onClick={() => setEditing(def)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </RobotoTooltip>
                  <RobotoTooltip title="Delete definition">
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`delete ${def.name}`}
                        onClick={() => setDeleting(def)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </RobotoTooltip>
                </>
              );
            },
          },
        ],
        dynamicColumns: [],
        preferenceOrder: DEFAULT_VISIBLE_COLUMN_IDS,
      }),
    [],
  );

  // Metric definitions are a Roboto Resource and this view already composes
  // `ResourceView.Root`, so drive the table through `useResourceView`. We only
  // need its `table` today; the hook leaves room to add filters later.
  const { table } = useResourceView<MetricDefinitionRecord>({
    getRowId: (row) => row.metric_id,
    defaultVisibleColumnIds: DEFAULT_VISIBLE_COLUMN_IDS,
  });

  // `useAllMetricDefinitions` drains pagination and returns the full set
  // client-side, but `DataTable` renders exactly the rows it's handed and
  // drives paging via `currentPage`/`pageSize`. Slice the full array into
  // the current page and report whether a further page exists.
  // TODO(ENG-2582): paginate
  const allRows = React.useMemo(() => data ?? [], [data]);
  const { currentPage, pageSize } = table;
  const pageRows = React.useMemo(() => {
    const start = currentPage * pageSize;
    return allRows.slice(start, start + pageSize);
  }, [allRows, currentPage, pageSize]);
  const hasNextPage = (currentPage + 1) * pageSize < allRows.length;

  if (error) {
    return (
      <Alert severity="error">
        Failed to load definitions: {error.message}
      </Alert>
    );
  }

  return (
    <div className={styles.tableSlot}>
      <ResourceView.Root>
        <DataTable<MetricDefinitionRecord>
          table={table}
          columns={columns}
          data={pageRows}
          hasNextPage={hasNextPage}
          totalRowCount={allRows.length}
          loading={deriveLoadingState(query)}
          noRowsContent={
            // No filters yet, so the table is only ever empty for want of data.
            <ResourceView.EmptyState
              hasActiveFilters={false}
              noData={{
                icon: ENTITY_ICONS.metric,
                title: "No metric definitions yet",
                description:
                  "A definition appears the first time an action or the SDK publishes a metric. Add its description and unit here.",
              }}
              noMatches={{
                icon: ENTITY_ICONS.metric,
                title: "No matching metric definitions",
                description: "Try adjusting or clearing your filters.",
              }}
            />
          }
        />
      </ResourceView.Root>

      {editing && (
        <EditDefinitionDialog
          definition={editing}
          orgId={orgId}
          onClose={() => setEditing(null)}
        />
      )}

      {deleting && (
        <DeleteDefinitionDialog
          definition={deleting}
          orgId={orgId}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

function DeleteDefinitionDialog({
  definition,
  orgId,
  onClose,
}: {
  definition: MetricDefinitionRecord;
  orgId: string | undefined;
  onClose: () => void;
}) {
  const deleteMutation = useDeleteMetricDefinition();

  const handleDelete = () => {
    deleteMutation.mutate(
      { name: definition.name, orgId },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete metric definition</DialogTitle>
      <DialogContent dividers>
        <DialogContentText>
          This permanently deletes <strong>{definition.name}</strong> and all
          its historical data for the whole organization. This action cannot be
          undone.
        </DialogContentText>
        {deleteMutation.error && (
          <Alert severity="error" className={styles.alert}>
            Failed to delete definition: {deleteMutation.error.message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          loading={deleteMutation.isPending}
          onClick={handleDelete}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function EditDefinitionDialog({
  definition,
  orgId,
  onClose,
}: {
  definition: MetricDefinitionRecord;
  orgId: string | undefined;
  onClose: () => void;
}) {
  const [description, setDescription] = React.useState(
    definition.description ?? "",
  );
  const [unit, setUnit] = React.useState(definition.unit ?? "");
  const updateMutation = useUpdateMetricDefinition();

  const handleSave = () => {
    const trimmedUnit = unit.trim();
    updateMutation.mutate(
      {
        name: definition.name,
        // An emptied field means "unitless", which the server stores as NULL —
        // so send null rather than "" to avoid persisting an empty-string unit.
        request: { description, unit: trimmedUnit === "" ? null : trimmedUnit },
        orgId,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit {definition.name}</DialogTitle>
      <DialogContent dividers>
        <TextField
          className={styles.field}
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />
        <TextField
          className={styles.field}
          label="Unit"
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
          fullWidth
          helperText="Unit of measure for this metric's values, e.g. %, ms, m/s. Leave empty if unitless."
        />
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          loading={updateMutation.isPending}
          onClick={handleSave}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
